/**
 * Artefact lineage on the client (ADR-21).
 *
 * A deliverable is a lineage of artefacts sharing a format, identified by `lineageId` and
 * numbered by `revision`. The model declares a revision in the tool call, the backend records it
 * on the artefact and on the `ToolOutput` item, and everything the client shows is derived from
 * those items — never from a second lookup, and never from an inference of its own.
 *
 * `latestRevisions` is the ONE fold behind every "files for this turn" surface. A bespoke flatten
 * beside it is how the incident happened: the gallery listed four tiles for two deliverables
 * because a flatten cannot know that one artefact replaced another.
 */

import { ToolOutputs } from '~api';

/**
 * @desc The part of a `ToolOutput` item the fold reads: whether it points at an artefact, and
 * what that artefact says about its lineage. Structural rather than the item union itself, so
 * the fold serves the timeline, an admin view and a test fixture from one implementation, and
 * returns the caller's own item type rather than a lossy projection of it.
 */
export type ArtifactItemLike = {
  artifactId: string | null;
  artifact?: ToolOutputs.ArtifactItemMeta<string>;
};

/** @desc An item the fold has established points at an artefact. */
export type ArtifactDelivery<T extends ArtifactItemLike> = T & { artifactId: string };

/**
 * @desc The current deliverables among a list of output items, in the order they were delivered.
 *
 * Four rules, in order:
 * 1. items with no artefact behind them are not deliverables;
 * 2. one artefact is one deliverable however many items point at it — a byte-identical
 *    re-emission is deduplicated at the store, so two items legitimately share an id;
 * 3. a lineage is represented by its highest revision (ties, and artefacts with no declared
 *    revision, by the later item);
 * 4. an artefact named by another item's `supersedes` is out, whatever its own item says —
 *    which is what covers a pre-existing artefact, revised this turn, whose own item predates
 *    lineage meta entirely.
 *
 * `derivedFrom` is deliberately absent from all four: provenance records where a file came
 * from, not what it replaces.
 *
 * Applying this to a growing prefix of a turn's items yields the right answer at every step, so
 * a streaming turn can re-fold on each event and a superseded tile leaves the moment its
 * replacement lands.
 */
export const latestRevisions = <T extends ArtifactItemLike>(
  items: readonly T[],
): Array<ArtifactDelivery<T>> => {
  const delivered = new Map<string, { item: ArtifactDelivery<T>; order: number }>();

  items.forEach((item, order) => {
    const { artifactId } = item;
    if (artifactId === null) return;
    if (delivered.has(artifactId)) return;
    delivered.set(artifactId, { item: item as ArtifactDelivery<T>, order });
  });

  const superseded = new Set<string>();
  for (const { item } of delivered.values()) {
    if (item.artifact?.supersedes) superseded.add(item.artifact.supersedes);
  }

  const heads = new Map<string, { item: ArtifactDelivery<T>; order: number }>();
  for (const entry of delivered.values()) {
    const { artifactId, artifact } = entry.item;
    if (superseded.has(artifactId)) continue;

    const lineage = artifact?.lineageId ?? artifactId;
    const head = heads.get(lineage);
    if (!head) {
      heads.set(lineage, entry);
      continue;
    }

    const revision = artifact?.revision ?? 0;
    const current = head.item.artifact?.revision ?? 0;
    if (revision > current || (revision === current && entry.order > head.order)) {
      heads.set(lineage, entry);
    }
  }

  return [...heads.values()].sort((a, b) => a.order - b.order).map((entry) => entry.item);
};

/**
 * @desc The file name a download offers: `<name>.<format>` for an artefact whose deliverable was
 * named at generation time, and the id-based fallback for documents that predate declared names.
 * The signed URL's own `Content-Disposition` is built the same way server-side; this keeps the
 * same-origin `download` hint from disagreeing with it.
 */
export const artifactDownloadName = (artifactId: string, format: string, name?: string): string =>
  `${name ?? `artifact-${artifactId}`}.${format}`;
